import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { ReportSummary } from 'api/candidates/types/Report';
import { HARNESS_FLAGS } from 'api/harness/harnessFlags';
import useHarnessFlag from 'api/harness/useHarnessFlag';
import useAuthGateState from 'hooks/useAuthGateState';
import { usePersonalChecksBannerDismissed } from './usePersonalChecksBannerDismissed';
import { getLatestReport, hasEligibleReportStatus } from './utils';
import { AUTH_METHODS } from '../../constants';

export { getLatestReport } from './utils';

interface CheckrPersonalBannerReduxState {
  portal?: {
    reports?: ReportSummary[];
    applicantId?: string;
  };
}

type PersonalBannerAttributes = {
  account_id?: string[];
  user_email?: string;
};

export type CheckrPersonalBannerState = {
  /**
   * True once the Harness flag for the Profiles banner has resolved (ready,
   * cache, or timeout). Sibling banners should defer rendering until this
   * is true.
   */
  isFlagLoaded: boolean;
  /**
   * True when treatment is `on` and shared eligibility gates pass. Drives
   * CheckrPersonalProfileBanner.
   */
  isNewVisible: boolean;
  /**
   * Dismisses the banner for the current candidate, persisting the choice in
   * localStorage. After calling, both visibility flags flip to false.
   */
  dismiss: () => void;
  /** Latest report's account ID — used for analytics on the new banner. */
  accountId: string | undefined;
  candidateId: string | undefined;
  reportId: string | undefined;
} & (
  | { isVisible: false; variant: undefined }
  | {
      isVisible: true;
      variant: 'setupProfile';
    }
);

/**
 * Single source of truth for Profiles banner state: one Harness evaluation
 * on the user traffic type (split key = applicantId ?? candidateId) with
 * `account_id` + `user_email` attributes, shared eligibility gates, and
 * visibility for both the legacy banner (`isVisible`, treatment `legacy`)
 * and the new banner (`isNewVisible`, treatment `on`).
 *
 * Sibling banners (ReviewCTA) use this to stay mutually exclusive with the
 * legacy banner — defer rendering when either `!isFlagLoaded` or `isVisible`.
 */
export const useCheckrPersonalBannerState = (): CheckrPersonalBannerState => {
  const reports = useSelector(
    (state: CheckrPersonalBannerReduxState) => state.portal?.reports,
  );
  const applicantId = useSelector(
    (state: CheckrPersonalBannerReduxState) => state.portal?.applicantId,
  );
  const {
    candidateLogin: { authMethod, candidateEmail: userEmail },
  } = useAuthGateState();

  const latestReport = useMemo(() => getLatestReport(reports), [reports]);
  const latestAccountId = latestReport?.accountId;
  const candidateId = latestReport?.candidateId;
  const reportId = latestReport?.id;

  // All unique account IDs across the candidate's reports.
  const accountIds = useMemo(
    () =>
      Array.from(
        new Set((reports ?? []).map(r => r.accountId).filter(Boolean)),
      ),
    [reports],
  );

  const attributes: PersonalBannerAttributes = useMemo(
    () => ({
      ...(accountIds.length > 0 ? { account_id: accountIds } : undefined),
      ...(userEmail ? { user_email: userEmail } : undefined),
    }),
    [accountIds, userEmail],
  );

  const { treatment, isResolved } = useHarnessFlag({
    flagKey: HARNESS_FLAGS.PERSONAL_BANNER,
    splitKey: applicantId ?? candidateId,
    attributes,
  });

  const { isDismissed, dismiss } = usePersonalChecksBannerDismissed();

  const base = {
    dismiss,
    accountId: latestAccountId,
    candidateId,
    reportId,
  };

  // While the flag is still loading we cannot say whether either banner will
  // render, so report both as not visible with `isFlagLoaded: false` so
  // callers can distinguish "not yet known" from "definitely not visible".
  if (!isResolved) {
    return {
      ...base,
      isVisible: false,
      isNewVisible: false,
      isFlagLoaded: false,
      variant: undefined,
    };
  }

  const meetsSharedGates =
    Boolean(applicantId) &&
    Boolean(latestAccountId) &&
    !isDismissed &&
    hasEligibleReportStatus(latestReport);

  const isLegacyVisible =
    meetsSharedGates &&
    treatment === 'legacy' &&
    authMethod !== AUTH_METHODS.CHECKR_PROFILE;

  const isNewVisible = meetsSharedGates && treatment === 'on';

  if (isLegacyVisible) {
    return {
      ...base,
      isVisible: true,
      isNewVisible: false,
      isFlagLoaded: true,
      variant: 'setupProfile',
    };
  }

  return {
    ...base,
    isVisible: false,
    isNewVisible,
    isFlagLoaded: true,
    variant: undefined,
  };
};
